import React, { useEffect, useMemo, useRef, useState } from "react";
import { Dialog, DialogContent, Grid, IconButton, Box, CircularProgress } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";

const isPdf = (url) => {
  if (!url) return false;
  const clean = url.split("?")[0].toLowerCase();
  return clean.endsWith(".pdf");
};

const ImageGallery = ({ images = [] }) => {
  const [open, setOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);

  // Cache: original pdf url -> blobUrl
  const [pdfBlobUrls, setPdfBlobUrls] = useState({});
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState(null);

  // Para evitar race conditions si cambias rápido entre items
  const fetchTokenRef = useRef(0);

  const selectedUrl = useMemo(() => {
    return selectedImageIndex !== null ? images[selectedImageIndex] : null;
  }, [images, selectedImageIndex]);

  const selectedPdfSrc = useMemo(() => {
    if (!selectedUrl) return null;
    if (!isPdf(selectedUrl)) return selectedUrl; // imagen
    return pdfBlobUrls[selectedUrl] || selectedUrl; // blobUrl si existe, si no URL directa
  }, [selectedUrl, pdfBlobUrls]);

  const fetchPdfAsBlobUrl = async (url) => {
    const myToken = ++fetchTokenRef.current;
    setPdfLoading(true);
    setPdfError(null);

    try {
      const res = await fetch(url, { mode: "cors" });
      if (fetchTokenRef.current !== myToken) return; // hubo otro fetch después

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }

      const blob = await res.blob();
      if (fetchTokenRef.current !== myToken) return;

      const blobUrl = URL.createObjectURL(blob);

      setPdfBlobUrls((prev) => {
        // si ya existía, revocamos el anterior
        if (prev[url]) URL.revokeObjectURL(prev[url]);
        return { ...prev, [url]: blobUrl };
      });
    } catch (e) {
      // OJO: si falla por CORS, igual podemos caer a iframe directo (URL original)
      setPdfError(e?.message || "No se pudo cargar el PDF");
    } finally {
      if (fetchTokenRef.current === myToken) setPdfLoading(false);
    }
  };

  const handleOpen = async (index) => {
    setSelectedImageIndex(index);
    setOpen(true);

    const url = images[index];
    if (isPdf(url) && !pdfBlobUrls[url]) {
      await fetchPdfAsBlobUrl(url);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedImageIndex(null);
    setPdfLoading(false);
    setPdfError(null);
    // no revocamos cache aquí; si quieres liberar memoria al cerrar, dímelo
  };

  const handlePrev = async () => {
    setPdfError(null);
    setSelectedImageIndex((prevIndex) => {
      const nextIndex = (prevIndex - 1 + images.length) % images.length;
      return nextIndex;
    });
  };

  const handleNext = async () => {
    setPdfError(null);
    setSelectedImageIndex((prevIndex) => {
      const nextIndex = (prevIndex + 1) % images.length;
      return nextIndex;
    });
  };

  // Cuando cambias de imagen en el modal, si es PDF y no está en cache, lo buscamos
  useEffect(() => {
    if (!open) return;
    if (!selectedUrl) return;
    if (!isPdf(selectedUrl)) return;
    if (pdfBlobUrls[selectedUrl]) return;

    fetchPdfAsBlobUrl(selectedUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedUrl]);

  // Cleanup al desmontar: revocar todos los blob urls
  useEffect(() => {
    return () => {
      Object.values(pdfBlobUrls).forEach((u) => {
        try {
          URL.revokeObjectURL(u);
        } catch {}
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <Grid container spacing={2}>
        {images.map((image, index) => (
          <Grid item xs={4} sm={3} md={2} key={index}>
            {isPdf(image) ? (
              <Box
                onClick={() => handleOpen(index)}
                sx={{
                  width: "100%",
                  aspectRatio: "1 / 1",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "#f5f5f5",
                  border: "1px solid rgba(0,0,0,0.12)",
                  borderRadius: 1,
                  cursor: "pointer",
                }}
                title="PDF"
              >
                <PictureAsPdfIcon sx={{ fontSize: 42 }} />
              </Box>
            ) : (
              <img
                src={image}
                alt={`image-${index}`}
                style={{ width: "100%", cursor: "pointer", display: "block" }}
                onClick={() => handleOpen(index)}
              />
            )}
          </Grid>
        ))}
      </Grid>

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogContent sx={{ padding: 0 }}>
          <Box
            sx={{
              position: "relative",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "black",
              minHeight: 300,
            }}
          >
            <IconButton
              onClick={handleClose}
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                zIndex: 3,
                color: "white",
              }}
            >
              <CloseIcon />
            </IconButton>

            {images?.length > 1 && (
              <>
                <IconButton
                  onClick={handlePrev}
                  sx={{
                    position: "absolute",
                    left: 8,
                    zIndex: 3,
                    color: "white",
                  }}
                  disabled={selectedImageIndex === null}
                >
                  <ArrowBackIosIcon />
                </IconButton>

                <IconButton
                  onClick={handleNext}
                  sx={{
                    position: "absolute",
                    right: 8,
                    zIndex: 3,
                    color: "white",
                  }}
                  disabled={selectedImageIndex === null}
                >
                  <ArrowForwardIosIcon />
                </IconButton>
              </>
            )}

            {/* CONTENT */}
            {selectedUrl ? (
              isPdf(selectedUrl) ? (
                <Box sx={{ width: "100%", bgcolor: "white" }}>
                  {pdfLoading && (
                    <Box
                      sx={{
                        height: 80,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1,
                      }}
                    >
                      <CircularProgress size={22} />
                      <span style={{ fontSize: 12 }}>Cargando PDF…</span>
                    </Box>
                  )}

                  {/* Si fetch falla (CORS), igual intentamos iframe directo con URL original */}
                  <iframe
                    src={selectedPdfSrc}
                    title="pdf"
                    width="100%"
                    height="750px"
                    style={{ border: "none", background: "white" }}
                  />

                  {pdfError && (
                    <Box sx={{ p: 1, fontSize: 12, color: "#b00020" }}>
                      No se pudo descargar el PDF para preview como Blob ({pdfError}). Se intentó mostrarlo directo desde
                      la URL; si se sigue descargando, revisa metadata/CORS del bucket.
                    </Box>
                  )}
                </Box>
              ) : (
                <img
                  src={selectedUrl}
                  alt="Selected"
                  style={{
                    width: "100%",
                    maxHeight: "85vh",
                    objectFit: "contain",
                    background: "black",
                    display: "block",
                  }}
                />
              )
            ) : null}
          </Box>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ImageGallery;